import React, { useState } from 'react';
import {
  Box, Typography, Autocomplete, TextField, Tabs, Tab, Button
} from '@mui/material';
import jsPDF from 'jspdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import SpinnerModal from './SpinnerModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';


const Tuning = () => {
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceDb, setSourceDb] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [fileNames, setFileNames] = useState([]);
  const [files, setFiles] = useState([]);
  const [apiResult, setApiResult] = useState(null);
  const [showSpinnerModal, setShowSpinnerModal] = useState(false);
  const [progress, setProgress] = useState(0);

  const sourceOptions = ['Stored Procedure', 'SQL', 'Python', 'PySpark'];
  const dropdownStyle = { width: '300px' };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFileUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    let validFiles = selectedFiles;
    if (validFiles.length > 0) {
      setFileNames(validFiles.map(f => f.name));
      setFiles(validFiles);

      // Upload each file to S3
      for (const file of validFiles) {
        const formData = new FormData();
        formData.append('file', file);
        try {
          await fetch('https://api.elevate.de.ltim.io/file_upload_for_tuning_and_suggestion/', {
            method: 'POST',
            body: formData,
          });
        } catch (error) {
          alert(`Error uploading ${file.name}: ${error.message}`);
        }
      }
    } else {
      setFileNames([]);
      setFiles([]);
    }
  };

  const getFileAccept = () => {
    if (sourceDb === 'Stored Procedure' || sourceDb === 'SQL') return '.sql';
    if (sourceDb === 'Python' || sourceDb === 'PySpark') return '.py';
    return '';
  };

  const handleSubmit = async () => {
    if (!sourceDb || files.length === 0) {
      setTabValue(1);
      return;
    }

    setShowSpinnerModal(true);
    setProgress(0);

    // Simulate progress
    let interval=null;
    interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 10000);

    // Prepare payload for API
    const payload = {
      type_of_metadata: sourceDb,
      file_name: fileNames[0], // Only first file is sent, adjust if needed
      is_documentation: false,
    };

    try {
      const response = await fetch('https://api.elevate.de.ltim.io/tuning_and_suggestion_task_process/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      clearInterval(interval);
      setProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        setShowSpinnerModal(false);
        setTabValue(1);

      } else {
        const data = await response.json();
        setApiResult(data);
        setTimeout(() => {
          setShowSpinnerModal(false);
          setTabValue(1);
        }, 2000);
      }
    } catch (error) {
      clearInterval(interval);
      setShowSpinnerModal(false);
      alert(`Error processing the file: ${error.message}`);
      setTabValue(1);
    }
  };

  const handleDownloadPDF = () => {
    if (!apiResult || !apiResult.report) return;
    const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text('Output', 10, 10);
        const resultContent = String(apiResult.report);
        doc.setFontSize(11);
        const pageWidth = doc.internal.pageSize.width;
        const margin = 10;
        const contentWidth = pageWidth - margin * 2;
        const lines = doc.splitTextToSize(resultContent, contentWidth);
        let cursorY = 20;
        const lineHeight = 7;
        const pageHeight = doc.internal.pageSize.height;
        lines.forEach((line) => {
          if (cursorY + lineHeight > pageHeight - margin) {
            doc.addPage();
            cursorY = margin;
          }
          doc.text(line, margin, cursorY);
          cursorY += lineHeight;
        });
        doc.save('Output.pdf');
  };
  const handleCopyCode = () => {
    if (!apiResult || !apiResult.report) {
      alert('No API result available to copy.');
      return;
    }
    const resultContent = String(apiResult.report);
    navigator.clipboard.writeText(resultContent).then(() => {
    //   alert('Result content copied to clipboard!');
    }).catch((error) => {
      console.error('Failed to copy text:', error);
    });
  };
  return (
    <Box sx={{ width: '100%', marginTop: -7 }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          pl: 0,
          '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
          '& .MuiTab-root': {
            minWidth: 120,
            fontWeight: 'bold',
            textTransform: 'none',
            paddingRight: 0,
            pl: 0,
          },
        }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 1 Input */}
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={24}
                  height={24}
                  borderRadius="50%"
                  bgcolor={apiResult?.report ? "#4caf50" : (tabValue === 0 ? "#1976d2" : "white")}
                  border="1.5px solid"
                  borderColor={apiResult?.report ? "#4caf50" : (tabValue === 0 ? "#1976d2" : "black")}
                  mr={1}
                  transition="all 0.2s"
                >
                  {apiResult?.report ? (
                    <CheckCircleIcon sx={{ color: "white", fontSize: 20 }} />
                  ) : (
                    <Typography variant="subtitle2" sx={{ color: tabValue === 0 ? "white" : "black", fontWeight: 600 }}>1</Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontWeight: tabValue === 0 ? 700 : 400,
                    color: tabValue === 0 ? "primary.main" : "black",
                    userSelect: "none",
                    transition: "all 0.2s"
                  }}
                >
                  Input
                </Typography>
              </Box>
              {/* Arrow */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 4,
                  ml: 1,
                  mr: 1,
                  mb: 0.6,
                }}
              >
                <Typography sx={{ color: "#bdbdbd", fontWeight: 700, fontSize: 44, lineHeight: 1 }}>
                  &rarr;
                </Typography>
              </Box>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingRight: 0, paddingLeft: 0 }}
        />
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 2 Result */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={24}
                height={24}
                borderRadius="50%"
                bgcolor={tabValue === 1 ? "#1976d2" : "white"}
                border="1.5px solid"
                borderColor={tabValue === 1 ? "#1976d2" : "black"}
                mr={1}
                transition="all 0.2s"
              >
                <Typography variant="subtitle2" sx={{ color: tabValue === 1 ? "white" : "black", fontWeight: 600 }}>2</Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: tabValue === 1 ? 700 : 400,
                  color: tabValue === 1 ? "primary.main" : "black",
                  userSelect: "none",
                  transition: "all 0.2s"
                }}
              >
                Result
              </Typography>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingLeft: 0 }}
        />
      </Tabs>


      {tabValue === 0 && (
        <Box sx={{ marginTop: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              width: '100%',
              gap: 2,
            }}
          >
            {/* Source Dropdown */}
            <Box sx={{ maxWidth: '300px' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Tuning and Suggestions<span style={{ color: 'red' }}>*</span>
              </Typography>
              <Autocomplete
                options={sourceOptions}
                value={sourceDb || ''}
                onChange={(event, newValue) => {
                  setSourceDb(newValue || '');
                  setFileNames([]);
                  setFiles([]);
                }}
                inputValue={sourceSearch}
                onInputChange={(event, newInputValue) => setSourceSearch(newInputValue)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Source Format" size="small" />
                )}
                sx={dropdownStyle}
              />

              {/* Upload File Button */}
              <Box sx={{ marginTop: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                  {sourceDb === 'Stored Procedure' || sourceDb === 'SQL'
                    ? 'Please upload the file which contains the source script'
                    : sourceDb === 'Python' || sourceDb === 'PySpark'
                      ? 'Please upload the file which contains the source script'
                      : 'Please upload the file according to the selected format'}<span style={{ color: 'red' }}>*</span>
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  component="label"
                  sx={{
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid black',
                    boxShadow: 'none',
                    height: '40px',
                  }}
                >
                  Upload File
                  <input
                    type="file"
                    hidden
                    onChange={handleFileUpload}
                    accept={getFileAccept()}
                  />
                </Button>
                {fileNames.length > 0 && (
                  <Box sx={{ marginTop: 1 }}>
                    {fileNames.map((name, idx) => (
                      <Typography key={idx} variant="subtitle2" sx={{ fontWeight: 'bold', color: 'green' }}>
                        Uploaded File: {name}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          {/* Show Submit button only after upload */}

          {fileNames.length > 0 && (
            <Box sx={{ display: 'flex', marginTop: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                  height: '40px',
                }}
              >
                SUBMIT
              </Button>
            </Box>
          )}
        </Box>
      )}


      {tabValue === 1 && (
        <Box sx={{ marginTop: 4 }}>
          <Box
            sx={{
              marginTop: 4,
              backgroundColor: '#f9f9f9',
              border: '1px solid #ccc',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: 2,
            }}
          >
            {apiResult?.report ? (
              <Typography
                variant="body1" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>

                {String(apiResult.report)}
              </Typography>
            ) : (
              <Typography variant="body2">No result available</Typography>
            )}

          </Box>

          {apiResult && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                }}
              >
                Download PDF
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCopyCode}
                sx={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                }}
                startIcon={<ContentCopyIcon />}
              >
                Copy Code
              </Button>
            </Box>
          )}
        </Box>
      )}

      <SpinnerModal
        open={showSpinnerModal}
        progress={progress}
        inProgressMessage="Result is being generated..."
        successMessage="Result Generated Successfully!"
        onClose={() => setShowSpinnerModal(false)}
      />
    </Box>
  );
};

export default Tuning;